import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Input, Button, Card, CardBody } from '@heroui/react';
import { Mail, ArrowLeft } from 'lucide-react';
import { apiClient } from '@/api/client';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await apiClient.post('/auth/forgot-password', { email });
      setSubmitted(true);
    } catch (err: unknown) {
      const axiosError = err as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      setError(
        axiosError?.response?.data?.message ??
          axiosError?.message ??
          'Something went wrong. Please try again.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: 'var(--bg-base)' }}
    >
      <div className="w-full max-w-sm">
        {/* Brand */}
        <div className="text-center mb-8">
          <h1
            className="text-3xl font-bold tracking-tight font-ui"
            style={{ color: 'var(--accent)' }}
          >
            Anvil
          </h1>
          <p className="mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
            Reset your password
          </p>
        </div>

        <Card
          classNames={{ base: 'border border-[var(--border)]' }}
          style={{ backgroundColor: 'var(--bg-surface)' }}
        >
          <CardBody className="p-6 gap-0">
            {submitted ? (
              <div className="flex flex-col gap-4">
                <div
                  className="text-sm px-3 py-2 rounded-lg border"
                  style={{
                    color: 'var(--green, #4ade80)',
                    backgroundColor: 'rgba(74, 222, 128, 0.08)',
                    borderColor: 'rgba(74, 222, 128, 0.2)',
                  }}
                >
                  If an account exists with that email, you'll receive a reset
                  link shortly. Check your inbox.
                </div>
                <Link
                  to="/login"
                  className="flex items-center gap-1 text-sm font-medium hover:underline"
                  style={{ color: 'var(--accent)' }}
                >
                  <ArrowLeft size={14} />
                  Back to sign in
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <p
                  className="text-sm mb-1"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Enter your email address and we'll send you a link to reset
                  your password.
                </p>

                <Input
                  type="email"
                  label="Email"
                  placeholder="you@company.com"
                  value={email}
                  onValueChange={setEmail}
                  isRequired
                  autoComplete="email"
                  startContent={
                    <Mail
                      size={16}
                      style={{ color: 'var(--text-muted)', flexShrink: 0 }}
                    />
                  }
                  classNames={{
                    inputWrapper:
                      'border border-[var(--border)] bg-[var(--bg-panel)] data-[hover=true]:border-[var(--border-em)] data-[focus=true]:border-[var(--accent)]',
                    input:
                      'text-[var(--text-primary)] placeholder:text-[var(--text-muted)]',
                    label: 'text-[var(--text-secondary)] text-sm',
                  }}
                  variant="bordered"
                />

                {error && (
                  <div
                    className="text-sm px-3 py-2 rounded-lg border"
                    style={{
                      color: 'var(--red)',
                      backgroundColor: 'rgba(248, 113, 113, 0.08)',
                      borderColor: 'rgba(248, 113, 113, 0.2)',
                    }}
                  >
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  color="primary"
                  isLoading={isLoading}
                  className="mt-1 font-medium"
                  fullWidth
                >
                  {isLoading ? 'Sending…' : 'Send Reset Link'}
                </Button>

                <Link
                  to="/login"
                  className="flex items-center gap-1 text-sm font-medium hover:underline mt-1"
                  style={{ color: 'var(--accent)' }}
                >
                  <ArrowLeft size={14} />
                  Back to sign in
                </Link>
              </form>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
